import React, { Fragment } from 'react';
import { FaCheck } from 'react-icons/fa';

const PROPERTY_TYPE_LABELS = {
  office_space: 'Office Space',
  retail_shop: 'Retail Shop',
  warehouse: 'Warehouse',
  factory: 'Factory',
  industrial_shed: 'Industrial Shed',
  coworking_space: 'Coworking Space',
  land: 'Land',
  cold_storage: 'Cold Storage',
  agricultural_land: 'Agricultural Land/Unit',
  hospitality_property: 'Hospitality Property',
};

// Mirrors labels / options in `InfrastructureLeasingListAssetPage.js` (read-only for display)
const PRICING_MODEL_BY_VALUE = {
  monthly_long_lease: 'Monthly rate (long-term lease)',
  daily_short_rental: 'Daily rate (short-term rental)',
  hourly_very_short: 'Hourly rate (very short-term)',
};

const DURATION_TYPE_BY_VALUE = {
  days: 'Days (short-term)',
  weeks: 'Weeks',
  months: 'Months',
  years: 'Years (long-term)',
};

const FURNISHING_BY_VALUE = {
  bare_shell: 'Bare shell',
  warm_shell: 'Warm shell',
  fully_furnished: 'Fully furnished',
  semi_furnished: 'Semi furnished',
};

const CONSTRUCTION_BY_VALUE = {
  ready_to_move: 'Ready to move',
  under_construction: 'Under construction',
  upcoming: 'Upcoming',
};

const OCCUPANCY_BY_VALUE = {
  vacant: 'Vacant',
  occupied: 'Occupied',
  partially_occupied: 'Partially occupied',
};

const INSURANCE_BY_VALUE = {
  insured: 'Insured',
  not_insured: 'Not insured',
  insurance_optional: 'Insurance optional',
};

const TRANSPORT_BY_VALUE = {
  buyer: 'Buyer / lessee',
  seller: 'Seller / lessor',
  shared: 'Shared',
  negotiable: 'Negotiable',
};

const OWNERSHIP_BY_VALUE = {
  direct_owner: 'Direct owner',
  broker_agent: 'Broker / agent',
  broker: 'Broker',
  broker_authorized: 'Authorized broker',
};

function str(v) {
  if (v == null) return '';
  return String(v).trim();
}

function isPresent(v) {
  if (v == null) return false;
  if (typeof v === 'boolean') return true;
  return str(v) !== '';
}

function labelMap(val, map) {
  if (!isPresent(val)) return null;
  const k = str(val);
  return map[k] != null ? map[k] : k.replace(/_/g, ' ');
}

/**
 * Renders a bullet row only when value is meaningful.
 * Label + value use a two-column grid (see `.ile-overview-bullets`).
 */
function Bullet({ label, value, dash = '—' }) {
  const empty = !isPresent(value) || (typeof value === 'string' && str(value) === '—');
  const display = empty
    ? dash
    : typeof value === 'string' || typeof value === 'number'
      ? str(value)
      : value;
  return (
    <li>
      <strong>{label}</strong>
      <span className="ile-overview-bullets__val">{display}</span>
    </li>
  );
}

function SpecSection({ title, children, emptyHint }) {
  if (!children) {
    return emptyHint ? (
      <p className="text-secondary small mb-0 py-1">{emptyHint}</p>
    ) : null;
  }
  return (
    <Fragment>
      {title && <h4 className="ile-spec-section-title">{title}</h4>}
      {children}
    </Fragment>
  );
}

function BoolLine({ label, on }) {
  if (on !== true && on !== false) return null;
  return (
    <li>
      <strong>{label}</strong>
      <span className="ile-overview-bullets__val">{on ? 'Yes' : 'No'}</span>
    </li>
  );
}

export function getMarketplaceKindLabel(listing) {
  if (listing?.listingType === 'machinery') {
    return 'Equipment & machinery';
  }
  if (listing?.listingType === 'property') {
    return 'Real estate & infrastructure';
  }
  return 'Listing';
}

/** Matches the six steps on `InfrastructureLeasingListAssetPage` (1–6). */
export const PUBLIC_SPEC_TAB_IDS = ['1', '2', '3', '4', '5', '6'];

/**
 * @param {string} tabId one of `PUBLIC_SPEC_TAB_IDS`
 * @param {{ listingType?: string }} listing
 */
export function getPublicSpecTabLabel(tabId, listing) {
  const t = str(tabId) || '1';
  const isM = listing?.listingType === 'machinery';
  if (t === '1') return 'Basic';
  if (t === '2') return 'Location';
  if (t === '3') return isM ? 'Asset' : 'Property';
  if (t === '4') return 'Pricing';
  if (t === '5') return 'Media';
  if (t === '6') return 'Review';
  return 'Basic';
}

/**
 * @param {string} specTab — `'1'`–`'6'` (same order as list-asset tabs)
 */
export function PublicListingSpecTabContent({ specTab, form, listing, txLabel, emptyBlockHint }) {
  if (!listing) {
    return null;
  }
  const t = str(specTab) || '1';
  if (t === '1') {
    return <BasicTab form={form} listing={listing} txLabel={txLabel} />;
  }
  if (t === '2') {
    return <LocationTab form={form} listing={listing} />;
  }
  if (t === '3') {
    return (
      <div className="ile-spec-step-property">
        <DetailsTab
          form={form}
          listing={listing}
          isMachinery={listing.listingType === 'machinery'}
          emptyBlockHint={emptyBlockHint}
        />
        <h4 className="ile-spec-section-title ile-spec-section-title--spaced">
          {listing.listingType === 'machinery' ? 'Key features' : 'Amenities & highlights'}
        </h4>
        <AmenitiesTab form={form} listing={listing} />
      </div>
    );
  }
  if (t === '4') {
    return <PricingTab form={form} listing={listing} />;
  }
  if (t === '5') {
    return <MediaTab form={form} listing={listing} />;
  }
  if (t === '6') {
    return <ReviewTab form={form} listing={listing} />;
  }
  return <BasicTab form={form} listing={listing} txLabel={txLabel} />;
}

function BasicTab({ form, listing, txLabel }) {
  const f = form && typeof form === 'object' ? form : {};
  const isMachinery = listing.listingType === 'machinery';
  return (
    <ul className="ile-overview-bullets">
      <li>
        <strong>Marketplace</strong>
        <span className="ile-overview-bullets__val">{getMarketplaceKindLabel(listing)}</span>
      </li>
      <Bullet label="Title" value={str(listing.title) || 'Untitled'} dash="—" />
      <li>
        <strong>Category</strong>
        <span className="ile-overview-bullets__val">{str(listing.categoryLabel) || '—'}</span>
      </li>
      <li>
        <strong>Key size / capacity</strong>
        <span className="ile-overview-bullets__val">{str(listing.keySpec) || '—'}</span>
      </li>
      {isPresent(f.legacyCategory) && (
        <li>
          <strong>Legacy category</strong>
          <span className="ile-overview-bullets__val">{str(f.legacyCategory).replace(/_/g, ' ')}</span>
        </li>
      )}
      {isPresent(f.ownershipType) && (
        <li>
          <strong>Ownership</strong>
          <span className="ile-overview-bullets__val">{labelMap(f.ownershipType, OWNERSHIP_BY_VALUE) || str(f.ownershipType)}</span>
        </li>
      )}
      <li>
        <strong>Transaction</strong>
        <span className="ile-overview-bullets__val">{txLabel ? txLabel(listing.transactionType) : '—'}</span>
      </li>
      {isMachinery && str(f.subCategory) && (
        <li>
          <strong>Equipment category</strong>
          <span className="ile-overview-bullets__val">{str(f.subCategory).replace(/_/g, ' ')}</span>
        </li>
      )}
      {isMachinery && str(f.subSubCategory) && (
        <li>
          <strong>Subcategory</strong>
          <span className="ile-overview-bullets__val">{str(f.subSubCategory)}</span>
        </li>
      )}
      {isMachinery && isPresent(f.assetSize) && (
        <li>
          <strong>Asset size (tier)</strong>
          <span className="ile-overview-bullets__val">{str(f.assetSize).replace(/_/g, ' ')}</span>
        </li>
      )}
      {isMachinery && f.assetSize === 'small' && isPresent(f.quantityAvailable) && (
        <li>
          <strong>Quantity available</strong>
          <span className="ile-overview-bullets__val">{str(f.quantityAvailable)}</span>
        </li>
      )}
    </ul>
  );
}

function LocationTab({ form, listing }) {
  const f = form && typeof form === 'object' ? form : {};
  return (
    <ul className="ile-overview-bullets">
      <li>
        <strong>Location (summary)</strong>
        <span className="ile-overview-bullets__val">
          {str(listing.locationLine) && listing.locationLine !== '—' ? listing.locationLine : '—'}
        </span>
      </li>
      {str(listing.countryName) && <Bullet label="Country" value={listing.countryName} />}
      {str(listing.stateName) && <Bullet label="State" value={listing.stateName} />}
      {str(listing.cityName) && <Bullet label="City" value={listing.cityName} />}
      {str(f.fullAddress) && (
        <li>
          <strong>Full address &amp; landmarks</strong>
          <span className="ile-overview-bullets__val">{str(f.fullAddress)}</span>
        </li>
      )}
      {isPresent(f.postalCode) && <Bullet label="PIN / postal code" value={str(f.postalCode)} />}
      {isPresent(f.industrialZone) && <Bullet label="Industrial zone / special area" value={str(f.industrialZone)} />}
    </ul>
  );
}

function PricingTab({ form, listing }) {
  const f = form && typeof form === 'object' ? form : {};
  return (
    <ul className="ile-overview-bullets">
      {isPresent(f.pricingModel) && (
        <li>
          <strong>Pricing model</strong>
          <span className="ile-overview-bullets__val">{labelMap(f.pricingModel, PRICING_MODEL_BY_VALUE) || str(f.pricingModel)}</span>
        </li>
      )}
      {isPresent(f.rentPerMonth) && <Bullet label="Rent (as entered, ₹)" value={str(f.rentPerMonth)} />}
      {isPresent(f.deposit) && <Bullet label="Security deposit (₹)" value={str(f.deposit)} />}
      {isPresent(f.maintenance) && <Bullet label="Maintenance (₹/month)" value={str(f.maintenance)} />}
      {isPresent(f.durationType) && (
        <li>
          <strong>Duration type</strong>
          <span className="ile-overview-bullets__val">{labelMap(f.durationType, DURATION_TYPE_BY_VALUE) || str(f.durationType)}</span>
        </li>
      )}
      {isPresent(f.availableDuration) && <Bullet label="Available for (total)" value={str(f.availableDuration)} />}
      {isPresent(f.minTenure) && <Bullet label="Minimum tenure" value={str(f.minTenure)} />}
      {isPresent(f.maxTenure) && <Bullet label="Maximum tenure" value={str(f.maxTenure)} />}
      {isPresent(f.lockin) && <Bullet label="Lock-in period" value={str(f.lockin)} />}
      {f.flexibleTenure === true && (
        <li>
          <strong>Flexible tenure</strong>
          <span className="ile-overview-bullets__val">Yes</span>
        </li>
      )}
      {isPresent(f.availableFrom) && <Bullet label="Available from" value={str(f.availableFrom)} />}
    </ul>
  );
}

function countGalleryImages(listing, form) {
  const f = form && typeof form === 'object' ? form : {};
  if (Array.isArray(listing?.imagePaths) && listing.imagePaths.length) {
    return listing.imagePaths.length;
  }
  if (listing?.imagePath) {
    return 1;
  }
  if (Array.isArray(f.images)) {
    const n = f.images.filter(Boolean).length;
    if (n) {
      return n;
    }
  }
  return 0;
}

function mediaEntryLabel(entry) {
  if (entry == null) {
    return '';
  }
  if (typeof entry === 'string') {
    return str(entry);
  }
  if (typeof entry === 'object' && entry && 'name' in entry) {
    return str(entry.name);
  }
  return 'File on record';
}

function hasMediaEntry(entry) {
  if (entry == null) {
    return false;
  }
  if (typeof entry === 'string') {
    return str(entry) !== '';
  }
  if (typeof entry === 'object') {
    if (str(entry.name) !== '') {
      return true;
    }
    if (str(entry.url) !== '') {
      return true;
    }
  }
  return false;
}

function MediaTab({ form, listing }) {
  const f = form && typeof form === 'object' ? form : {};
  const nImg = countGalleryImages(listing, f);
  const nVid = Array.isArray(f.videos) ? f.videos.filter(hasMediaEntry).length : 0;
  const docRows = [
    { key: 'floorPlan', label: 'Floor plan' },
    { key: 'drone', label: 'Drone view / aerial' },
    { key: 'complianceDocs', label: 'Compliance / ownership' },
  ];
  return (
    <div className="ile-spec-media">
      <p className="text-secondary small mb-2">
        Primary photos are shown in the gallery above. Counts and documents reflect what was saved with the listing.
      </p>
      <ul className="ile-overview-bullets">
        <li>
          <strong>Images / photos</strong>
          <span className="ile-overview-bullets__val">{nImg ? `${nImg} on listing` : '—'}</span>
        </li>
        <li>
          <strong>Videos</strong>
          <span className="ile-overview-bullets__val">{nVid ? `${nVid} on file` : '—'}</span>
        </li>
      </ul>
      <h4 className="ile-spec-section-title">Property &amp; compliance documents</h4>
      <ul className="ile-overview-bullets">
        {docRows.map(({ key, label }) => {
          const v = f[key];
          const has = hasMediaEntry(v);
          return (
            <li key={key}>
              <strong>{label}</strong>
              <span className="ile-overview-bullets__val">
                {has ? mediaEntryLabel(v) || 'Attached' : <span className="text-secondary">Not provided</span>}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function ReviewTab({ form, listing }) {
  const f = form && typeof form === 'object' ? form : {};
  const desc = str(listing?.description) || str(f.description);
  return (
    <div className="ile-spec-review">
      <h4 className="ile-spec-section-title">Detailed description</h4>
      <div className="ile-spec-review__desc">{desc || '—'}</div>
      <h4 className="ile-spec-section-title ile-spec-section-title--spaced">Nearby connectivity &amp; landmarks</h4>
      {isPresent(f.connectivity) ? (
        <p className="ile-spec-review__text mb-0">{str(f.connectivity)}</p>
      ) : (
        <p className="text-secondary small mb-0">Not provided</p>
      )}
      <h4 className="ile-spec-section-title ile-spec-section-title--spaced">Contact preferences</h4>
      <ul className="ile-overview-bullets">
        {isPresent(f.contactVisibility) ? (
          <li>
            <strong>Contact visibility</strong>
            <span className="ile-overview-bullets__val">{str(f.contactVisibility).replace(/_/g, ' ')}</span>
          </li>
        ) : (
          <li className="ile-overview-bullets__plain text-secondary small">Not specified</li>
        )}
      </ul>
    </div>
  );
}

function DetailsTab({ form, listing, isMachinery, emptyBlockHint }) {
  const f = form && typeof form === 'object' ? form : {};
  if (isMachinery) {
    return (
      <div className="ile-spec-detail-stacks">
        <SpecSection title="Identification & condition">
          <ul className="ile-overview-bullets">
            {isPresent(f.equipmentBrand) && <Bullet label="Brand" value={f.equipmentBrand} />}
            {isPresent(f.equipmentModel) && <Bullet label="Model" value={f.equipmentModel} />}
            {isPresent(f.equipmentMake) && <Bullet label="Make" value={f.equipmentMake} />}
            {isPresent(f.serialNumber) && <Bullet label="Serial number" value={f.serialNumber} />}
            {isPresent(f.manufactureYear) && <Bullet label="Year of manufacture" value={f.manufactureYear} />}
            {isPresent(f.equipmentCondition) && <Bullet label="Condition" value={f.equipmentCondition} />}
            {isPresent(f.operationalStatus) && <Bullet label="Operational status" value={f.operationalStatus} />}
            {isPresent(f.usageHours) && <Bullet label="Usage hours" value={f.usageHours} />}
          </ul>
        </SpecSection>
        <SpecSection title="Capacity & dimensions">
          <ul className="ile-overview-bullets">
            {isPresent(f.maxCapacity) && (
              <li>
                <strong>Capacity</strong>
                <span className="ile-overview-bullets__val">
                  {str(f.maxCapacity)} {str(f.capacityUnit)}
                </span>
              </li>
            )}
            {(isPresent(f.dimLength) || isPresent(f.dimWidth) || isPresent(f.dimHeight)) && (
              <li>
                <strong>Dimensions (L × W × H)</strong>
                <span className="ile-overview-bullets__val">
                  {[
                    str(f.dimLength) || '—',
                    str(f.dimWidth) || '—',
                    str(f.dimHeight) || '—',
                  ].join(' × ')}
                </span>
              </li>
            )}
            {isPresent(f.netWeight) && (
              <li>
                <strong>Net weight</strong>
                <span className="ile-overview-bullets__val">
                  {str(f.netWeight)} {labelMap(f.weightUnit, { kg: 'kg', tonnes: 'tonnes', lbs: 'lbs', g: 'g' }) || str(f.weightUnit)}
                </span>
              </li>
            )}
          </ul>
        </SpecSection>
        <SpecSection title="Location & mobility">
          <ul className="ile-overview-bullets">
            {isPresent(f.installationLocation) && <Bullet label="Installation location" value={f.installationLocation} />}
            {f.relocationPossible === true || f.relocationPossible === false ? (
              <BoolLine label="Relocation possible" on={f.relocationPossible} />
            ) : null}
            {isPresent(f.relocationCost) && <Bullet label="Relocation cost" value={f.relocationCost} />}
          </ul>
        </SpecSection>
        <SpecSection title="Operations">
          <ul className="ile-overview-bullets">
            {f.operatorIncluded === true || f.operatorIncluded === false ? (
              <BoolLine label="Operator included" on={f.operatorIncluded} />
            ) : null}
            {isPresent(f.operatorAddOnCost) && <Bullet label="Operator add-on (₹)" value={f.operatorAddOnCost} />}
            {isPresent(f.fuelType) && <Bullet label="Fuel / power type" value={f.fuelType} />}
            {isPresent(f.fuelConsumption) && <Bullet label="Fuel / energy consumption" value={f.fuelConsumption} />}
          </ul>
        </SpecSection>
        <SpecSection title="Insurance & transport">
          <ul className="ile-overview-bullets">
            {isPresent(f.insuranceStatus) && (
              <li>
                <strong>Insurance</strong>
                <span className="ile-overview-bullets__val">{labelMap(f.insuranceStatus, INSURANCE_BY_VALUE) || str(f.insuranceStatus)}</span>
              </li>
            )}
            {isPresent(f.insuranceCost) && <Bullet label="Insurance cost (₹)" value={f.insuranceCost} />}
            {isPresent(f.transportResponsibility) && (
              <li>
                <strong>Transport</strong>
                <span className="ile-overview-bullets__val">
                  {labelMap(f.transportResponsibility, TRANSPORT_BY_VALUE) || str(f.transportResponsibility)}
                </span>
              </li>
            )}
            {isPresent(f.transportCost) && <Bullet label="Transport cost (₹)" value={f.transportCost} />}
          </ul>
        </SpecSection>
        {emptyBlockHint && <p className="text-secondary small mt-2 mb-0">{emptyBlockHint}</p>}
      </div>
    );
  }
  // Property
  return (
    <div className="ile-spec-detail-stacks">
      <SpecSection title="Property specifications">
        <ul className="ile-overview-bullets">
          {isPresent(f.propertyType) && (
            <li>
              <strong>Property type</strong>
              <span className="ile-overview-bullets__val">
                {PROPERTY_TYPE_LABELS[str(f.propertyType)] != null
                  ? PROPERTY_TYPE_LABELS[str(f.propertyType)]
                  : str(f.propertyType).replace(/_/g, ' ')}
              </span>
            </li>
          )}
          {isPresent(f.carpetArea) && <Bullet label="Carpet area (sq. ft.)" value={f.carpetArea} />}
          {isPresent(f.builtUpArea) && <Bullet label="Built-up area (sq. ft.)" value={f.builtUpArea} />}
          {isPresent(f.landArea) && <Bullet label="Land / plot (sq. ft. or as entered)" value={f.landArea} />}
          {isPresent(f.zoning) && <Bullet label="Zoning / usage" value={f.zoning} />}
          {isPresent(f.furnishing) && (
            <li>
              <strong>Furnishing</strong>
              <span className="ile-overview-bullets__val">{labelMap(f.furnishing, FURNISHING_BY_VALUE) || str(f.furnishing)}</span>
            </li>
          )}
        </ul>
      </SpecSection>
      <SpecSection title="Infrastructure & utilities">
        <ul className="ile-overview-bullets">
          {f.power === true || f.power === false ? <BoolLine label="Power availability" on={f.power} /> : null}
          {f.power && isPresent(f.powerLoadKw) && <Bullet label="Power load (kW)" value={f.powerLoadKw} />}
          {f.water === true || f.water === false ? <BoolLine label="Water availability" on={f.water} /> : null}
          {f.loading === true || f.loading === false ? <BoolLine label="Loading / unloading" on={f.loading} /> : null}
          {isPresent(f.parking) && <Bullet label="Parking (spaces / nos.)" value={f.parking} />}
          {isPresent(f.parkingType) && <Bullet label="Parking type" value={labelMap(f.parkingType, { open: 'Open', covered: 'Covered', basement: 'Basement' }) || f.parkingType} />}
          {isPresent(f.roadAccessFt) && <Bullet label="Road access (ft)" value={f.roadAccessFt} />}
        </ul>
      </SpecSection>
      <SpecSection title="Compliance & approvals">
        <ul className="ile-overview-bullets">
          {f.cFire ? (
            <li>
              <strong>Fire safety</strong>
              <span className="ile-overview-bullets__val">Yes</span>
            </li>
          ) : null}
          {f.cEnv ? (
            <li>
              <strong>Environmental clearance</strong>
              <span className="ile-overview-bullets__val">Yes</span>
            </li>
          ) : null}
          {f.cTrade ? (
            <li>
              <strong>Trade / industrial license</strong>
              <span className="ile-overview-bullets__val">Yes</span>
            </li>
          ) : null}
          {f.cOcc ? (
            <li>
              <strong>Occupancy certificate</strong>
              <span className="ile-overview-bullets__val">Yes</span>
            </li>
          ) : null}
          {f.cPol ? (
            <li>
              <strong>Pollution clearance</strong>
              <span className="ile-overview-bullets__val">Yes</span>
            </li>
          ) : null}
          {!f.cFire && !f.cEnv && !f.cTrade && !f.cOcc && !f.cPol && (
            <li className="ile-overview-bullets__plain text-secondary small">No compliance flags were selected on the listing.</li>
          )}
        </ul>
      </SpecSection>
      <SpecSection title="Construction & occupancy">
        <ul className="ile-overview-bullets">
          {isPresent(f.construction) && (
            <li>
              <strong>Construction status</strong>
              <span className="ile-overview-bullets__val">{labelMap(f.construction, CONSTRUCTION_BY_VALUE) || str(f.construction)}</span>
            </li>
          )}
          {isPresent(f.occupancy) && (
            <li>
              <strong>Occupancy</strong>
              <span className="ile-overview-bullets__val">{labelMap(f.occupancy, OCCUPANCY_BY_VALUE) || str(f.occupancy)}</span>
            </li>
          )}
        </ul>
      </SpecSection>
      {emptyBlockHint && <p className="text-secondary small mt-2 mb-0">{emptyBlockHint}</p>}
    </div>
  );
}

function buildAmenitiesList(form, listing) {
  const f = form && typeof form === 'object' ? form : {};
  const isMachinery = listing?.listingType === 'machinery';
  if (isMachinery) {
    const out = [];
    if (isPresent(f.maxCapacity) && (isPresent(f.capacityUnit) || f.capacityUnit === '')) {
      out.push(`Capacity: ${str(f.maxCapacity)}${str(f.capacityUnit) ? ` ${str(f.capacityUnit)}` : ''}`);
    }
    if (isPresent(f.fuelType)) {
      out.push(`Fuel / power: ${str(f.fuelType)}`);
    }
    if (f.operatorIncluded === true) {
      out.push('Operator can be included');
    }
    if (f.relocationPossible === true) {
      out.push('Relocation is possible');
    }
    if (f.insuranceStatus) {
      out.push(`Insurance: ${labelMap(f.insuranceStatus, INSURANCE_BY_VALUE) || str(f.insuranceStatus)}`);
    }
    if (out.length === 0 && str(listing?.categoryLabel)) {
      out.push(str(listing.categoryLabel));
    }
    return out;
  }
  const out = [];
  if (f.power) {
    out.push(str(f.powerLoadKw) ? `Power: ${str(f.powerLoadKw)} kW` : 'Power available');
  }
  if (f.water) out.push('Water supply');
  if (f.loading) out.push('Loading / unloading access');
  if (isPresent(f.parking) || f.parkingType) {
    const p = [f.parking ? `Spaces: ${f.parking}` : null, f.parkingType || null].filter(Boolean).join(' — ');
    if (p) out.push(`Parking: ${p}`);
  }
  if (f.cFire) out.push('Fire safety');
  if (f.cEnv) out.push('Environmental clearance');
  if (f.cTrade) out.push('Trade / industrial license');
  if (f.cOcc) out.push('Occupancy certificate');
  if (f.cPol) out.push('Pollution clearance');
  if (isPresent(f.roadAccessFt)) out.push(`Road access: ${str(f.roadAccessFt)} ft`);
  if (f.furnishing) {
    out.push(`Furnishing: ${labelMap(f.furnishing, FURNISHING_BY_VALUE) || str(f.furnishing)}`);
  }
  if (out.length === 0 && str(listing?.categoryLabel)) {
    out.push(str(listing.categoryLabel));
  }
  return out;
}

function AmenitiesTab({ form, listing }) {
  const am = buildAmenitiesList(form, listing);
  if (!am.length) {
    return (
      <p className="text-secondary small mb-0">
        No feature or amenity highlights were saved, or the relevant fields are empty. See the description in the Review tab.
      </p>
    );
  }
  return (
    <div className="ile-spec-grid" style={{ gridTemplateColumns: '1fr' }} role="list">
      {am.map((label) => (
        <div key={label} className="ile-spec-item">
          <span className="ile-check" aria-hidden>
            <FaCheck />
          </span>
          <span>{label}</span>
        </div>
      ))}
    </div>
  );
}
